import {
  PaperAirplaneIcon,
  PhotoIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { Loading, Tooltip } from 'react-daisyui';
import classNames from 'classnames';
import './Template.styles.scss';
import AiBadge from 'src/pages/WhatsappApi/components/TemplatesTable/components/AiBadge/AiBadge.component';
import {
  isAiTemplateName,
  stripAiTemplateNamePrefix,
} from 'src/pages/WhatsappApi/components/TemplatesTable/TemplatesTable.functions';
import {
  tPredefinedVariableDefinition,
  tPredefinedVariables,
  tTemplates,
} from '../../../../../../../../pages/Pipe/Pipe.types.ts';
import { useWorkspaceContext } from '../../../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { ChatService } from '../../../../../../Chat.service.ts';
import { useToastContext } from '../../../../../../../../providers/ToastProvider/Toast.hook.ts';
import { eAlertType } from '../../../../../../../../providers/ToastProvider/Toast.enum.ts';
import {
  tWapiSendTemplateRequest,
  tWapiTemplateParameter,
} from '../../../../../../Chat.types.ts';
import { tWapiCopyCodeButton } from 'src/components/Chat/components/MessageBar/components/WabTemplatesVariablesAction/WabTemplatesVariablesAction.types.ts';
import { useMessageBarContext } from '../../../../MessageBar.hook.ts';
import { ChatEnum, ChatState } from '../../../../../../Chat.enum.ts';
import { useModalsContext } from '../../../../../../../../providers/ModalsProvider/Modals.context.ts';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useTemplatesTable from 'src/pages/WhatsappApi/components/TemplatesTable/TemplatesTable.hook.ts';
import { ExclamationTriangleIcon } from '@heroicons/react/16/solid';
import { useErrorHandler } from 'src/hooks/useErrorHandler.hook';
import {
  buildOutboundRefusalAlert,
  lookupOutboundRefusal,
} from 'src/utils/ErrorDirectory';
import {
  isSafeTemplateHeaderMediaUrl,
  resolveTemplateHeaderMediaUrl,
  templateHeaderRequiresMedia,
} from 'src/utils/templateMedia';
import { tComponent } from 'src/pages/WhatsappApi/WhatsappApi.types.ts';
import { useChatContext } from 'src/components/Chat/Chat.hook.ts';
import { withHumanAction } from 'src/components/Chat/Chat.humanAction';
import { useWapiPlanSendGuard } from 'src/components/Chat/useWapiPlanSendGuard.hook';
import { eChannelType } from 'src/providers/SocketsProvider/Sockets.enums';
import { useWhatsappApiContext } from 'src/pages/WhatsappApi/WhatsappApi.context';
import InactiveConversationModal from '../InactiveConversationModal/InactiveConversationModal.component.tsx';
import BlockedLeadModal from '../BlockedLeadModal/BlockedLeadModal.component';
import { tSentTemplateCounter } from 'src/pages/WhatsappApi/components/TemplatesTable/SentTemplateCounter.types.ts';
import { useWapiAnalytics } from 'src/hooks/analytics/useWapiAnalytics.hook';
import { WAPI_EVENTS } from 'src/hooks/analytics/wapiAnalytics.constants';
import TemplateCarouselPreview from 'src/pages/WhatsappApi/components/TemplateCarouselPreview/TemplateCarouselPreview.component.tsx';
import {
  useWalletSendGate,
  isTemplateFreeAtSource,
} from 'src/pages/Wallet/hooks/useWalletSendGate';
import { useTopUpRoleGate } from 'src/pages/Wallet/hooks/useTopUpRoleGate';
import { useTopUpModal } from 'src/pages/Wallet/hooks/useTopUpModal';
import ComposerNoBalanceBar from 'src/pages/Wallet/components/ComposerNoBalanceBar/ComposerNoBalanceBar.component';

type templateProps = {
  template: tTemplates;
  predefinedVariables?: tPredefinedVariables;
  variableDefinitions?: tPredefinedVariableDefinition[];
  /**
   * Costo de enviar ESTA plantilla a ESTE destinatario, ya formateado
   * (RET-1694, pantalla 10 del prototipo: cada plantilla con su precio ANTES
   * de intentar enviar).
   *
   * Es distinto del `costLabel` de `useWalletSendGate` (RET-1806): aquél se
   * calcula al PULSAR enviar y sólo se pinta si el saldo no alcanza. Éste se
   * muestra siempre, en la lista, para que el operador elija sabiendo el
   * precio. Los dos cotizan con la misma escalera
   * (`templateTypeToWalletCategory`), así que no pueden discrepar.
   *
   * Llega por prop y no de un hook propio porque el precio depende de
   * categoría × país: con un solo destinatario el país es fijo, así que el
   * padre cotiza una vez por categoría (≤4) en vez de una por renglón.
   *
   * `undefined` es "no lo sabemos" —wallet apagada, o la cotización falló— y
   * entonces la columna del precio dice "Con costo" (D-4: ya no es un badge
   * ni una píldora, es la celda `.Template__Cost` del renglón). Nunca se
   * pinta un cero: "gratis" es una afirmación sobre dinero que no se puede
   * inventar.
   */
  formattedCost?: string;
};

const getComponentBody = (components: tComponent[]): string => {
  return components.find((cpmn) => cpmn.type === 'BODY')?.text || '';
};

const getComponentHeader = (components: tComponent[]): string | null => {
  const headerComponent = components.find((cpmn) => cpmn.type === 'HEADER');

  if (!headerComponent) return null;

  return (
    headerComponent.text ??
    headerComponent.example?.url_example?.[0] ??
    headerComponent.example?.header_handle?.[0] ??
    null
  );
};

/**
 * Declared `format` of the template's top-level HEADER, or `undefined` when
 * there is none. Read as a plain string and never cast to `eFormatType`: the
 * value comes straight from Meta's payload, so an unrecognized one has to
 * fall through benignly rather than be asserted into an enum.
 *
 * A carousel resolves to `undefined` — its headers live nested inside
 * `cards[]`, and that is the right answer: the gate below is about the single
 * top-level header the variables modal knows how to attach media for
 * (BUG-866).
 */
const getComponentHeaderFormat = (
  components: tComponent[],
): string | undefined =>
  components.find((cpmn) => cpmn.type === 'HEADER')?.format;

export default function Template(props: templateProps) {
  const { currentWorkspaceId } = useWorkspaceContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { addAlert } = useToastContext();
  const {
    setSelectedAction,
    setWapiVariables,
    setScheduledWapiTemplate,
    isSchedulePickerMode,
    setIsSchedulePickerMode,
  } = useMessageBarContext();
  const [loading, setLoading] = useState<boolean>(false);
  const [isInactiveModalOpen, setIsInactiveModalOpen] =
    useState<boolean>(false);
  const [isBlockedLeadModalOpen, setIsBlockedLeadModalOpen] =
    useState<boolean>(false);
  const [isImageBroken, setIsImageBroken] = useState<boolean>(false);
  const { setTemplateCounter } = useWhatsappApiContext();
  const { t } = useTranslation(['chat/chat', 'errors/errors']);
  const { handleError } = useErrorHandler();
  const { parseComponentIntoJson } = useTemplatesTable();
  const { selectedChat, setSelectedChat } = useChatContext();
  const { trackWapiEvent } = useWapiAnalytics();
  const { blockWapiPlanSend, isWapiPlanSendBlocked } = useWapiPlanSendGuard();
  // RET-1806, DS-03 §5: el aviso + bloqueo de saldo insuficiente en el
  // compositor 1 a 1. `checkBalance` es un no-op (deja pasar el envío) con
  // el flag `billing_wapi-wallet` apagado o mientras carga — ver el hook.
  // El id del chat es el ÁMBITO del veredicto: el aviso es sobre el envío a
  // ESTE contacto, y al cambiar de conversación deja de aplicar (DS-03 prohíbe
  // que sea un banner fijo). La fila no se desmonta al cambiar de chat, así que
  // sin esto la barra viajaba.
  const { isBlocked, costLabel, shortfall, checkBalance } = useWalletSendGate(
    selectedChat?.id,
  );
  const { openOneOffWithAmount, modal: topUpModal } = useTopUpModal();
  // RET-1806, fix del bloqueante de arquitectura de la ronda 2: dos clics
  // mientras `checkBalance` está en vuelo disparaban dos cotizaciones y, con
  // saldo suficiente, dos envíos — el guard de `loading` no alcanzaba porque
  // `setLoading(true)` corre DESPUÉS del await de la cotización. Un ref se
  // actualiza de forma sincrónica (a diferencia de `loading`, que es estado
  // de React y no refleja el segundo clic hasta el siguiente render), así
  // que corta el segundo clic exactamente en la ventana donde `loading`
  // todavía es `false`.
  const isSendingRef = useRef(false);
  // Sólo Owner/Admin recargan (DS-03 §3, mismo criterio que DS-16/DS-17;
  // RET-2115: único lugar que decide, `useTopUpRoleGate`). El componente de la
  // barra no lee el rol, lo decide quien la monta.
  const { canTopUp: canTopUpBalance } = useTopUpRoleGate();
  // UC-09 / RET-1806 fix del bloqueante #1 (ronda 1): la MISMA verdad de
  // "gratis en origen" que la columna de precio de abajo pinta (D-4:
  // `.Template__Cost--free` / "Gratis"), calculada UNA vez y reusada para esa
  // columna, el tracking (`isFreeMessage`) y el gate (`checkBalance`) — así
  // las tres superficies nunca pueden divergir.
  const isFreeAtSource = isTemplateFreeAtSource({
    templateType: props.template.template_type,
    isChatActive: selectedChat?.state === ChatState.ACTIVE,
    hasFreeEntryPoint: !!selectedChat?.freeEntryPointData?.hasFreeEntryPoint,
  });

  /**
   * RET-2234 · en modo AGENDAR esta fila no puede afirmar "Gratis".
   *
   * `isFreeAtSource` se resuelve contra AHORA, pero en este modo el envío
   * dispara en una fecha que todavía no se ha elegido — la elige la pantalla
   * siguiente. Antes del 1-oct las dos pantallas mentían igual y por eso
   * concordaban; con el corte fechado (`D-7`) el panel resuelve contra el
   * instante de disparo y esta fila seguiría resolviendo contra ahora, así
   * que una UTILITY agendada para después del corte se leía "Gratis" aquí y
   * "Con costo" una pantalla después. Dos respuestas para lo mismo, que es
   * justo lo que `CO-11` prohíbe.
   *
   * La salida es la del ruling de la afirmación asimétrica: no se afirma lo
   * que no se puede respaldar. El PRECIO sí se sigue pintando —no depende de
   * la ventana, y es el diferenciador entre plantillas que `DS-12` pide—, lo
   * que se retira es la palabra "Gratis". Sólo puede prometer de menos: si el
   * disparo acaba cayendo antes del corte, el panel dirá "Gratis" y la fila
   * habrá mostrado su tarifa.
   */
  const badgeClaimsFree = isFreeAtSource && !isSchedulePickerMode;

  const {
    componentBody,
    componentHeader,
    componentHeaderFormat,
    componentJson,
  } = useMemo(() => {
    const parsedComponents = parseComponentIntoJson(props.template.components);

    return {
      componentBody: getComponentBody(parsedComponents),
      componentHeader: getComponentHeader(parsedComponents),
      componentHeaderFormat: getComponentHeaderFormat(parsedComponents),
      componentJson: parsedComponents,
    };
  }, [props.template.components, parseComponentIntoJson]);

  useEffect(() => {
    setIsImageBroken(false);
  }, [componentHeader]);

  const tooltipMessage = `Header: ${componentHeader ? componentHeader?.slice(0, 32) : ' '} \n Body: ${componentBody}`;

  const onlyNumberVariableRegex = /{{\s*\d+\s*}}/;
  const hasOnlyNumberVariable =
    onlyNumberVariableRegex.test(componentHeader || '') ||
    onlyNumberVariableRegex.test(componentBody || '');

  /**
   * Validates if the inactive conversation modal should be shown before sending a template.
   * If the user has sent a template, the lead hasn't replied, and the chat is inactive,
   * the modal is shown. Otherwise, sendTemplate is called directly.
   */

  /**
   * Handles the confirmation action from the InactiveConversationModal.
   * Closes the modal and proceeds to send the template.
   */
  async function handleConfirmInactiveModal(): Promise<void> {
    setIsInactiveModalOpen(false);
    await executeSendTemplate();
  }

  /**
   * Executes the template sending without validation checks.
   * This function is called after user confirmation in modals.
   */
  async function executeSendTemplate() {
    if (!currentWorkspaceId || !selectedLeadForChatModal?.handle) {
      return;
    }
    // RET-2349 (R5): a template always goes out by WhatsApp API. The plan
    // refuses it before the balance check is consulted, not instead of it.
    if (blockWapiPlanSend(eChannelType.WHATSAPP_API)) return;

    // El guard sincrónico va ANTES que nada más — ver la nota del ref, arriba.
    if (isSendingRef.current) {
      return;
    }
    isSendingRef.current = true;

    try {
      // RET-1806, DS-03: el embudo de las DOS entradas vivas de este camino
      // (clic directo y confirmar el modal de conversación inactiva) — las
      // dos llaman a executeSendTemplate(), así que el veredicto de saldo
      // aplica a ambas desde un solo lugar. Corre DESPUÉS de los gates de hoy
      // (lead bloqueado / conversación inactiva ya decidieron no llegar aquí).
      const canSend = await checkBalance({
        templateType: props.template.template_type,
        recipient: selectedLeadForChatModal.handle,
        isFreeAtSource,
      });
      if (!canSend) {
        return;
      }

      await sendTemplateNow();
    } finally {
      isSendingRef.current = false;
    }
  }

  /** El envío real, una vez que el veredicto de saldo ya dejó pasar. */
  async function sendTemplateNow() {
    if (!currentWorkspaceId || !selectedLeadForChatModal?.handle) {
      return;
    }
    // RET-2349: silent — `executeSendTemplate` already showed the toast.
    if (isWapiPlanSendBlocked(eChannelType.WHATSAPP_API)) return;

    const isMarketing =
      props.template.template_type === 'MARKETING' ||
      props.template.template_type === 'FOLLOWING';

    setLoading(true);
    try {
      const payload: tWapiSendTemplateRequest = {
        to: selectedLeadForChatModal.handle,
        template_id: props.template.template_id,
      };

      // INT-1538 U10: a person sending from the chat; recorded first.
      await withHumanAction(currentWorkspaceId, selectedChat, () =>
        ChatService.sendWapiFollowTemplate(currentWorkspaceId, payload),
      );

      if (isFreeAtSource) {
        trackWapiEvent(
          isMarketing
            ? WAPI_EVENTS.MESSAGE_TEMPLATE_MARKETING_FREE_SENT
            : WAPI_EVENTS.MESSAGE_TEMPLATE_UTILITY_FREE_SENT,
          {
            templateId: props.template.template_id,
            templateName: props.template.name,
            recipientNumber: selectedLeadForChatModal.handle,
            conversationId: selectedChat?.id ?? '',
            isFreeMessage: true,
          },
        );
      } else {
        trackWapiEvent(
          isMarketing
            ? WAPI_EVENTS.MESSAGE_TEMPLATE_MARKETING_SENT
            : WAPI_EVENTS.MESSAGE_TEMPLATE_UTILITY_SENT,
          {
            templateId: props.template.template_id,
            templateName: props.template.name,
            recipientNumber: selectedLeadForChatModal.handle,
            conversationId: selectedChat?.id ?? '',
            isFreeMessage: false,
          },
        );
      }

      // Update template counter in WhatsappApi context
      setTemplateCounter((prev: tSentTemplateCounter | undefined) => ({
        hasSentTemplate: prev?.hasSentTemplate ?? false,
        conversationExpiresAt: prev?.conversationExpiresAt ?? '',
        marketingWhileClosed: isMarketing
          ? (prev?.marketingWhileClosed || 0) + 1
          : (prev?.marketingWhileClosed ?? 0),
        utilityWhileClosed: isMarketing
          ? (prev?.utilityWhileClosed ?? 0)
          : (prev?.utilityWhileClosed || 0) + 1,
      }));

      // Update sentTemplateCounter in selectedChat
      if (selectedChat?.sentTemplateCounter) {
        setSelectedChat((prev) => {
          if (!prev || !prev.sentTemplateCounter) return prev;
          return {
            ...prev,
            sentTemplateCounter: {
              ...prev.sentTemplateCounter,
              marketingWhileClosed: isMarketing
                ? (prev.sentTemplateCounter.marketingWhileClosed || 0) + 1
                : prev.sentTemplateCounter.marketingWhileClosed,
              utilityWhileClosed: isMarketing
                ? prev.sentTemplateCounter.utilityWhileClosed
                : (prev.sentTemplateCounter.utilityWhileClosed || 0) + 1,
            },
          };
        });
      }
    } catch (error: unknown) {
      // A send the outbound gate refused (423: the plan is paused, or the send
      // could not be tied to its workspace) reads the same whatever its code.
      // handleError would paint a refusal code the directory has not registered
      // yet with the server's English `message` (the INT-1103 pass-through,
      // which every other error keeps).
      const refusal = lookupOutboundRefusal(error);
      let messageKey: string | undefined;
      if (refusal) {
        addAlert(buildOutboundRefusalAlert(refusal, t));
        messageKey = refusal.messageKey;
      } else {
        messageKey = handleError(
          error,
          `${t('wabTemplatesVariablesAction.error.text')}`,
        )?.messageKey;
      }
      trackWapiEvent(
        isMarketing
          ? WAPI_EVENTS.MESSAGE_TEMPLATE_MARKETING_FAILED
          : WAPI_EVENTS.MESSAGE_TEMPLATE_UTILITY_FAILED,
        {
          templateId: props.template.template_id,
          templateName: props.template.name,
          recipientNumber: selectedLeadForChatModal?.handle ?? '',
          errorMessage:
            messageKey ||
            (error instanceof Error ? error.message : String(error)),
        },
      );
    } finally {
      setLoading(false);
      setSelectedAction(ChatEnum.CHAT);
    }
  }

  function handleScheduleClick(e: React.SyntheticEvent) {
    e.stopPropagation();
    if (hasOnlyNumberVariable) return;
    setScheduledWapiTemplate(props.template);
    setSelectedAction(ChatEnum.WABTEMPLATES_SCHEDULED);
  }

  function handleScheduleKeyDown(e: React.KeyboardEvent) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    handleScheduleClick(e);
  }

  function handleScheduleFromPicker() {
    if (hasOnlyNumberVariable) return;
    setScheduledWapiTemplate(props.template);
    setIsSchedulePickerMode(false);
    setSelectedAction(ChatEnum.WABTEMPLATES_SCHEDULED);
  }

  async function sendTemplate() {
    /*
      AQUÍ NO VA UN `isBlocked ||`, y la omisión es el arreglo.

      DS-03 pide que el control se ATENÚE con la barra visible, y eso ya lo hace
      el `aria-disabled` de la tarjeta (área H) — que atenúa sin matar el clic.
      Cortocircuitar además el envío convertía el aviso en una puerta trancada:
      los cuatro `setIsBlocked` viven dentro de `checkBalance`, así que impedir
      que `checkBalance` vuelva a correr impedía también que el veredicto
      caducara. El cliente recargaba, volvía, y el botón estaba muerto hasta
      cerrar y reabrir el selector.

      La cotización es la autoridad en CADA intento. Reintentar con el saldo
      todavía corto vuelve a bloquear (el área H no se debilita: sigue sin
      enviar); reintentar después de recargar pasa. Y preguntar de nuevo es lo
      único que no puede quedar rancio — limpiar la barra al cerrar el modal de
      recarga habría dejado el veredicto igual de viejo ante cualquier otra
      forma de recargar: otra pestaña, otro seller, el widget del home.
    */
    if (
      loading ||
      !currentWorkspaceId ||
      !selectedLeadForChatModal?.handle ||
      hasOnlyNumberVariable
    ) {
      return;
    }

    const { template } = props;

    if (!template?.name || !template.components || !template.template_id) {
      addAlert({
        type: eAlertType.ERROR,
        message: `${t('wabTemplatesVariablesAction.error.text')}`,
      });
      return;
    }
    /*
      BUG-866: "¿esta plantilla exige media en el header?" se decide por el
      `format` DECLARADO del HEADER, no por la forma de la URL de ejemplo.

      Cuando la plantilla se creó en Business Manager, Meta devuelve el ejemplo
      del header como un handle de upload opaco
      (`4:MDFN…:aW1hZ2UvanBlZw==:ARbV…`) en vez de una URL de CDN. Ese string no
      empieza por `https://`, así que el predicado de abajo respondía "no lleva
      media", el modal nunca abría, y el envío salía como `{to, template_id}`
      pelado — sin `media` y sin `parameters`. Meta lo tumbaba con 132012
      (`wba-10038`) y el cliente no veía absolutamente nada.

      El `||` con el predicado de la URL se conserva a propósito (KTD8): sólo
      puede AMPLIAR lo que abre el modal, nunca restringirlo, así que las filas
      legacy sin `format` siguen entrando por donde entraban y la no-regresión
      sale gratis.
    */
    if (
      template.components.includes('{{') ||
      templateHeaderRequiresMedia(componentHeaderFormat) ||
      componentHeader?.startsWith('https://')
    ) {
      const parameters: tWapiTemplateParameter[] = [];
      const templateVarRegex = /{{(.*?)}}/g;
      let match;

      while ((match = templateVarRegex.exec(template.components)) !== null) {
        const variableName = match[1].trim();
        parameters.push({
          name: variableName,
          value: '',
          type: 'text',
        });
      }

      // BUG-817: resolve the header media from `components`, the way the
      // campaign, bulk and scheduled paths already do. The flat url_example
      // column only ever holds a value for templates created inside
      // Leadsales — the sync rewrites it to Go's zero value for everything
      // synced from Meta — so it wins when it parses and the resolver fills
      // the gap for everything else.
      let urlExample: string[] = [];
      const resolvedHeaderMedia = resolveTemplateHeaderMediaUrl(componentJson);
      if (resolvedHeaderMedia) {
        urlExample = [resolvedHeaderMedia];
      }

      if (template?.url_example) {
        try {
          const parsedColumn: unknown = JSON.parse(template.url_example);
          const columnEntries = Array.isArray(parsedColumn)
            ? parsedColumn
            : parsedColumn && typeof parsedColumn === 'object'
              ? Object.values(parsedColumn as Record<string, unknown>)
              : [];
          // Same trust boundary as the components path: the column is
          // written by our own backend today, but nothing here enforces that,
          // so an unhosted or wrong-format value must not auto-attach either.
          const columnUrls = columnEntries.filter(
            (value): value is string =>
              typeof value === 'string' &&
              !!value &&
              isSafeTemplateHeaderMediaUrl(value),
          );
          if (columnUrls.length > 0) {
            // First entry in key order — the producer marshals a map keyed
            // "0", "1", and only one header media is ever sent (KTD5).
            urlExample = [columnUrls[0]];
          }
        } catch {
          // malformed column — keep the resolver's result rather than
          // rejecting this async click handler's promise (KTD7)
        }
      }

      const copyCodeButtons: tWapiCopyCodeButton[] = [];
      let filename = '';
      try {
        const parsed: {
          type: string;
          example?: { filename?: string };
          buttons?: { type: string; text: string }[];
        }[] = JSON.parse(template.components);
        const buttonsComponent = parsed.find((c) => c.type === 'BUTTONS');
        buttonsComponent?.buttons?.forEach((btn, idx) => {
          if (btn.type === 'COPY_CODE') {
            copyCodeButtons.push({ index: idx, text: btn.text });
          }
        });
        filename =
          parsed.find((c) => c.type === 'HEADER')?.example?.filename ?? '';
      } catch {
        // malformed components — proceed without copy-code buttons
      }

      setWapiVariables({
        title: template.name,
        header: componentHeader,
        // BUG-866: el modal gatea su propio uploader con el mismo predicado.
        // Sin el `format` abre pero sigue ciego, y manda igual sin `media` —
        // el mismo 132012, ahora con dos clics.
        format: componentHeaderFormat,
        template_id: template.template_id,
        template_type: template.template_type,
        parameters: parameters,
        template: componentBody ?? '',
        url_example: urlExample,
        filename,
        predefinedVariables: props.predefinedVariables,
        variableDefinitions: props.variableDefinitions,
        copyCodeButtons:
          copyCodeButtons.length > 0 ? copyCodeButtons : undefined,
      });
      setSelectedAction(ChatEnum.WABTEMPLATES_VARIABLES);
      return;
    }

    const undeliveredMessage = selectedChat?.undeliveredMessage;
    if (!undeliveredMessage?.canSendMessages) {
      setIsBlockedLeadModalOpen(true);
      return;
    }

    // Count templates according to chat state
    const isInactive = selectedChat?.state === ChatState.INACTIVE;
    const counter = selectedChat?.sentTemplateCounter;
    const totalTemplatesWhileClosed =
      (counter?.marketingWhileClosed || 0) + (counter?.utilityWhileClosed || 0);
    // If user has sent 1 template or more, show inactive modal
    if (isInactive && totalTemplatesWhileClosed > 0) {
      setIsInactiveModalOpen(true);
      return;
    }

    await executeSendTemplate();
  }

  return (
    <>
      <div
        style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
      >
        <button
          onClick={
            isSchedulePickerMode ? handleScheduleFromPicker : sendTemplate
          }
          className="Template"
          // RET-1806, fix del bloqueante de arquitectura de la ronda 2 (DS-01):
          // este <button> es un contenedor COMPARTIDO — envolvía también el
          // afordance de PROGRAMAR (el <span role="button"> del reloj, más
          // abajo). El atributo HTML `disabled` en un <button> suprime los
          // eventos de clic de TODOS sus descendientes, así que bloqueaba
          // también programar — algo que DS-01 nunca autoriza («en campañas
          // programadas se reserva cuando la campaña se envía, no al
          // programarla») y que este ticket declaró fuera de alcance. Se
          // reemplaza por `aria-disabled`, que atenúa (área H) sin tocar el
          // despacho de eventos: la atenuación del ENVÍO queda a cargo del
          // guard explícito dentro de `sendTemplate()`, y el afordance de
          // programar sigue funcionando exactamente igual, por mouse y por
          // teclado.
          aria-disabled={isBlocked ? 'true' : undefined}
        >
          <Tooltip message={tooltipMessage} className="flex-1 min-w-0">
            <div
              className={`Template__Container ${
                isBlocked ? 'Template__Container--blocked' : ''
              }`}
            >
              {hasOnlyNumberVariable && (
                <Tooltip
                  color="warning"
                  position="right"
                  message={
                    'Por el momento Leadsales no soporta plantillas con variables numéricas.'
                  }
                >
                  <ExclamationTriangleIcon className="Template__Container__WarningIcon" />
                </Tooltip>
              )}
              <div
                className={`Template__Container__Badge ${
                  props.template.template_type === 'UTILITY'
                    ? 'Template__Container__Badge--utility'
                    : 'Template__Container__Badge--marketing'
                }`}
              >
                {props.template.template_type === 'FOLLOWING' ||
                props.template.template_type === 'MARKETING'
                  ? 'Marketing'
                  : props.template.template_type === 'UTILITY' && 'Seguimiento'}
              </div>
              <h3 className="Template__Container__Title flex items-center gap-2">
                {stripAiTemplateNamePrefix(props?.template?.name ?? '')}

                {isAiTemplateName(props?.template?.name ?? '') ? (
                  <AiBadge
                    className="Template__Container__AiTag"
                    label={t('templates.aiGenerated')}
                  />
                ) : null}
              </h3>
              <div className="Template__Container__Message">
                <p className="Template__Container__Message__Header">
                  {componentHeader?.startsWith('https://') ? (
                    isImageBroken ? (
                      <PhotoIcon className="h-6 w-6 text-secondary-content/60 shrink-0" />
                    ) : (
                      <img
                        className="h-6 object-cover object-center rounded-md shrink-0"
                        src={componentHeader ?? ''}
                        alt="image"
                        onError={() => setIsImageBroken(true)}
                      />
                    )
                  ) : (
                    componentHeader ||
                    t('chat/chat:wabTemplatesVariablesAction.noHeader')
                  )}
                </p>
                <p className="Template__Container__Message__Body truncate">
                  {componentBody}
                </p>
              </div>
              <TemplateCarouselPreview components={componentJson} />
            </div>
          </Tooltip>

          {!hasOnlyNumberVariable && !isSchedulePickerMode && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleScheduleClick}
              onKeyDown={handleScheduleKeyDown}
              className="Template__Schedule"
            >
              <ClockIcon className="Template__Schedule__Icon" />
              <span className="Template__Schedule__Label">
                {t('wabTemplateActions.schedule')}
              </span>
            </span>
          )}
          {/*
            RET-2106, D-4: el precio es una COLUMNA del renglón, hijo directo
            de este <button>, no una píldora dentro de `.Template__Container`
            (donde vivía junto al badge de categoría — D-6, que no se toca).
            Va después de `.Template__Schedule` (cuando se renderiza) y antes
            de la acción de enviar, así su borde derecho lo fija esa acción,
            presente en todo renglón, y no depende de si se muestra
            "Programar" (las plantillas con variable numérica no lo muestran
            — ver `hasOnlyNumberVariable` arriba). En modo agendar (sin
            Programar ni enviar) queda como último hijo del botón.

            "Gratis" gana siempre sobre el monto: `isFreeAtSource` es la MISMA
            verdad que ya decide el gate de saldo, así que nunca puede
            afirmar un precio sobre un mensaje que no cuesta.

            RET-2234 · pero se lee `badgeClaimsFree`, no `isFreeAtSource` a
            secas: en modo AGENDAR el envío dispara en el FUTURO, y una
            utility agendada para después del corte llegaría cobrada. La
            columna sólo puede prometer de menos. (El badge que este guardia
            cubría se retiró en RET-2106, que movió el precio a esta columna;
            el guardia viaja con él.)
          */}
          <span
            className={classNames('Template__Cost', {
              'Template__Cost--free': badgeClaimsFree,
              'Template__Cost--paid': !badgeClaimsFree,
              'Template__Cost--blocked': isBlocked,
            })}
          >
            {badgeClaimsFree ? 'Gratis' : (props.formattedCost ?? 'Con costo')}
          </span>
          {!isSchedulePickerMode &&
            (loading ? (
              <Loading className="Template__Action" color="primary" size="sm" />
            ) : (
              <Tooltip
                message={t('wabTemplateActions.send')}
                position="top"
                className="shrink-0"
              >
                <PaperAirplaneIcon
                  data-tour="wab-template-send"
                  className={`Template__Action w-5 h-5 text-secondary-content/80 transition-colors hover:text-primary ${
                    isBlocked ? 'Template__Action--blocked' : ''
                  }`}
                />
              </Tooltip>
            ))}
        </button>
      </div>
      {isBlocked && (
        <ComposerNoBalanceBar
          costLabel={costLabel}
          onTopUp={
            // Sin faltante no se abre a prellenar: `shortfall` solo es null si
            // el veredicto ya caduco (cambio de chat), y en ese caso la barra
            // tampoco esta.
            canTopUpBalance && shortfall
              ? () => openOneOffWithAmount(shortfall)
              : undefined
          }
        />
      )}
      {topUpModal}
      <InactiveConversationModal
        isOpen={isInactiveModalOpen}
        onClose={() => setIsInactiveModalOpen(false)}
        onConfirm={handleConfirmInactiveModal}
      />
      <BlockedLeadModal
        isOpen={isBlockedLeadModalOpen}
        // SIN `onConfirm`, y la ausencia es el punto: el modal no tiene camino
        // de confirmacion. Su unico boton («Entendido») cierra, y su copy es un
        // alto en firme — «no envies mas mensajes a este contacto o tu cuenta
        // puede ser bloqueada». Aqui habia un `onConfirm` que reenviaba la
        // plantilla y que el modal nunca invocaba: codigo que describia una UI
        // que no existe, y que al leerlo hacia creer que el bloqueo era
        // saltable. Si algun dia se decide permitir el envio forzado, es una
        // decision de producto y empieza por el copy del modal.
        onClose={() => setIsBlockedLeadModalOpen(false)}
      />
    </>
  );
}
